import { useId, useState } from 'react';
import Card from '@/components/Card';
import type { Recommendation } from '@/data/recommendations';

interface RecommendationCardProps {
  recommendation: Recommendation;
  /** Stagger index for the shared Card entrance animation. */
  index: number;
}

/**
 * A single LinkedIn recommendation styled as a countersigned endorsement.
 * Leads with the standout line (the signal); the full text — split into
 * paragraphs — reveals on demand and stays in the DOM while collapsed (SEO).
 */
const RecommendationCard = ({ recommendation, index }: RecommendationCardProps): JSX.Element => {
  const { highlight, quote, name, title, relationship, date } = recommendation;
  const [expanded, setExpanded] = useState<boolean>(false);
  const fullTextId = useId();

  const hasFullText = quote.trim() !== highlight.trim();
  const paragraphs = quote.split(/\n{2,}/);

  return (
    <Card index={index} className="flex flex-col">
      <figure className="flex h-full flex-col">
        <div className="flex-1">
          <p className="eyebrow">{relationship}</p>

          <blockquote className="mt-4 font-display text-xl leading-snug text-[color:var(--fg)]">
            {highlight}
          </blockquote>

          {hasFullText && (
            <>
              <button
                type="button"
                onClick={(): void => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                aria-controls={fullTextId}
                className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs font-medium text-primary-ink transition-opacity duration-150 hover:opacity-70 active:opacity-60 dark:text-primary"
              >
                {expanded ? 'Show less' : 'Show full recommendation'}
                <span aria-hidden="true">{expanded ? '↑' : '↓'}</span>
              </button>

              {/* Full text stays in the DOM (crawlable) and is revealed on demand. */}
              <div
                id={fullTextId}
                hidden={!expanded}
                className="mt-4 space-y-3 border-t border-[color:var(--hairline)] pt-4 leading-relaxed text-[color:var(--fg-muted)]"
              >
                {paragraphs.map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </>
          )}
        </div>

        <figcaption className="mt-6 border-t border-[color:var(--hairline)] pt-4">
          <span className="block font-display font-bold text-[color:var(--fg)]">{name}</span>
          <span className="eyebrow mt-2 block">{title}</span>
          <span className="mt-1 block font-mono text-xs text-[color:var(--fg-muted)]">{date}</span>
        </figcaption>
      </figure>
    </Card>
  );
};

export default RecommendationCard;
